/** Drag-and-drop + click + clipboard-paste file intake. */
import { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { IconCloudUpload, IconClipboard } from '@tabler/icons-react';
import { cn } from '../../lib/cn.ts';

interface UploadDropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function UploadDropzone({ onFiles, disabled }: UploadDropzoneProps) {
  const onDrop = useCallback((accepted: File[]) => onFiles(accepted), [onFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
    accept: { 'image/*': [], 'video/*': [] },
  });

  // Clipboard paste (Ctrl/Cmd+V) — route image blobs through the same handler.
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;
      const files: File[] = [];
      for (const item of e.clipboardData.items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) onFiles(files);
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [onFiles]);

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed bg-surface px-6 py-14 text-center shadow-sm transition duration-200 ease-standard',
        isDragActive
          ? 'border-accent bg-accent/10'
          : 'border-line hover:border-accent/60',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input {...getInputProps()} />
      <div className="grid h-16 w-16 place-items-center rounded-2xl bg-accent text-white shadow-sm">
        <IconCloudUpload className="h-8 w-8" />
      </div>
      <div>
        <p className="text-lg font-semibold text-fg">
          {isDragActive ? 'Drop to add photos' : 'Drag photos & videos here'}
        </p>
        <p className="mt-1 text-sm text-fg-2">
          or click to browse
        </p>
      </div>
      <p className="inline-flex items-center gap-1.5 text-xs text-muted">
        <IconClipboard className="h-3.5 w-3.5" /> You can also paste from the clipboard
      </p>
    </div>
  );
}
